"use client";

import { LazySection } from "@/components/common/lazy-section";
import { SkeletonLoader } from "@/components/ui/skeleton";
import { MapPin } from "lucide-react";
import dynamic from "next/dynamic";

// Lazy load the NearbyPlacesMap component with Next.js dynamic import (no SSR)
const NearbyPlacesMap = dynamic(() => import("./nearby-places-map").then(module => module.NearbyPlacesMap), {
	ssr: false,
	loading: () => <MapLoadingSkeleton />,
});

interface LazyNearbyPlacesMapProps {
	center: { lat: number; lng: number };
	coordinates: [number, number];
	className?: string;
}

// Enhanced loading skeleton that matches the map layout
export function MapLoadingSkeleton({ className }: { className?: string }) {
	return (
		<div className={className}>
			{/* Category filters skeleton */}
			<div className="mb-4 flex flex-wrap gap-2">
				{Array.from({ length: 16 }).map((_, i) => (
					<SkeletonLoader key={i} height="32px" width="80px" className="rounded-full" />
				))}
			</div>

			{/* Map container skeleton */}
			<div className="w-full h-[400px] relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
				<div className="flex flex-col items-center gap-3 text-gray-400">
					<MapPin className="w-8 h-8 animate-pulse" />
					<div className="text-sm font-medium">Loading nearby places...</div>
					<div className="flex gap-1">
						{Array.from({ length: 3 }).map((_, i) => (
							<div
								key={i}
								className="w-2 h-2 bg-gray-300 rounded-full animate-bounce"
								style={{ animationDelay: `${i * 0.2}s` }}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

export function LazyNearbyPlacesMap({ center, coordinates, className }: LazyNearbyPlacesMapProps) {
	return (
		<LazySection
			className={className}
			rootMargin="100px" // Load when 100px away from viewport
			threshold={0.1}
			fallback={<MapLoadingSkeleton className={className} />}
		>
			<NearbyPlacesMap center={center} coordinates={coordinates} className={className} />
		</LazySection>
	);
}
